import { Property } from '../api/get-calendar-data-for-house';
import { WeekData } from '../types/booking-data';
import { BookingRequest, BookingRequestAction } from '../types/booking-request';
import { advanceStep, STEP_SELECT_A_HOUSE } from '../types/steps';

export function bookingReducer(
	state: BookingRequest = {
		currentStep: STEP_SELECT_A_HOUSE,
		bookingData: {
			houseId: null,
			houseTitle: null,
			focusDate: new Date(),
			startOfStay: null,
			maxPersons: 4,
			numberOfPersons: 1,
			numberOfNights: 7,
			weekData: null,
		},
		personalDetails: {
			fullName: '',
			email: '',
			telephone: '',
			feedback: '',
			agreeToTerms: false,
			confirmRequest: false,
		},
		property: null,
	},
	action: BookingRequestAction
): BookingRequest {
	switch ( action.type ) {
		case 'UPDATE_PROPERTY_DATA':
			if ( action.property && state.bookingData.startOfStay ) {
				const weekData = weekDataForCurrentState( action.property, state.bookingData.startOfStay, state.bookingData.numberOfNights );

				if (
					weekData &&
					state.bookingData.weekData !== weekData
				) {
					return {
						...state,
						bookingData: {
							...state.bookingData,
							weekData: weekData,
						},
						property: action.property,
						propertyDataNetworkFailure: action.failure,
					};
				}
			}

			return {
				...state,
				property: action.property,
				propertyDataNetworkFailure: action.failure,
			};
		case 'UPDATE_CURRENT_STEP':
			return {
				...state,
				currentStep: action.currentStep,
			};
		case 'UPDATE_BOOKING_FAILURE':
			return {
				...state,
				bookingFailure: action.bookingFailure,
			};
		case 'UPDATE_HOUSE':
			return {
				...state,
				currentStep: advanceStep( STEP_SELECT_A_HOUSE ),
				property: null,
				propertyDataNetworkFailure: undefined,
				bookingData: {
					...state.bookingData,
					houseId: action.houseId,
					houseTitle: action.houseTitle,
					maxPersons: action.maxPersons,
					weekData: null,
				},
			};
		case 'UPDATE_FOCUS_DATE':
			return {
				...state,
				bookingData: {
					...state.bookingData,
					focusDate: action.focusDate,
				},
			};
		case 'UPDATE_START_OF_STAY':
			if (
				!action.startOfStay ||
				!state.property
			) {
				return {
					...state,
					bookingData: {
						...state.bookingData,
						startOfStay: action.startOfStay,
						startOfStayAria: action.startOfStayAria,
						startOfStayFailure: action.startOfStayFailure,
						weekData: null,
					},
				};
			}

			return {
				...state,
				bookingData: {
					...state.bookingData,
					startOfStay: action.startOfStay,
					startOfStayAria: action.startOfStayAria,
					startOfStayFailure: action.startOfStayFailure,
					weekData: weekDataForCurrentState( state.property, action.startOfStay, state.bookingData.numberOfNights ),
				},
			};
		case 'UPDATE_START_OF_STAY_AND_FOCUS_DATE':
			if ( !state.property ) {
				return {
					...state,
					bookingData: {
						...state.bookingData,
						startOfStay: action.date,
						startOfStayFailure: undefined,
						focusDate: action.date,
						startOfStayAria: action.startOfStayAria,
					},
				};
			}

			return {
				...state,
				bookingData: {
					...state.bookingData,
					startOfStay: action.date,
					startOfStayFailure: undefined,
					focusDate: action.date,
					startOfStayAria: action.startOfStayAria,
					weekData: weekDataForCurrentState( state.property, action.date, state.bookingData.numberOfNights ),
				},
			};
		case 'UPDATE_NUMBER_OF_NIGHTS':
			if ( !state.bookingData.startOfStay || !state.property ) {
				return {
					...state,
					bookingData: {
						...state.bookingData,
						numberOfNights: action.numberOfNights,
						numberOfNightsFailure: action.numberOfNightsFailure,
					},
				};
			}

			return {
				...state,
				bookingData: {
					...state.bookingData,
					numberOfNights: action.numberOfNights,
					numberOfNightsFailure: action.numberOfNightsFailure,
					weekData: weekDataForCurrentState( state.property, state.bookingData.startOfStay, action.numberOfNights ),
				},
			};
		case 'UPDATE_NUMBER_OF_NIGHTS_FAILURE':
			return {
				...state,
				bookingData: {
					...state.bookingData,
					numberOfNightsFailure: action.numberOfNightsFailure,
				},
			};
		case 'UPDATE_NUMBER_OF_PERSONS':
			return {
				...state,
				bookingData: {
					...state.bookingData,
					numberOfPersons: action.numberOfPersons,
					numberOfPersonsFailure: action.numberOfPersonsFailure,
				},
			};
		case 'UPDATE_NUMBER_OF_PERSONS_FAILURE':
			return {
				...state,
				bookingData: {
					...state.bookingData,
					numberOfPersonsFailure: action.numberOfPersonsFailure,
				},
			};
		case 'UPDATE_FULL_NAME':
			return {
				...state,
				personalDetails: {
					...state.personalDetails,
					fullName: action.fullName,
					fullNameFailure: action.fullNameFailure,
				},
			};
		case 'UPDATE_EMAIL':
			return {
				...state,
				personalDetails: {
					...state.personalDetails,
					email: action.email,
					emailFailure: action.emailFailure,
				},
			};
		case 'UPDATE_TELEPHONE':
			return {
				...state,
				personalDetails: {
					...state.personalDetails,
					telephone: action.telephone,
					telephoneFailure: action.telephoneFailure,
				},
			};
		case 'UPDATE_FEEDBACK':
			return {
				...state,
				personalDetails: {
					...state.personalDetails,
					feedback: action.feedback,
				},
			};
		case 'UPDATE_AGREE_TO_TERMS':
			return {
				...state,
				personalDetails: {
					...state.personalDetails,
					agreeToTerms: action.agreeToTerms,
					agreeToTermsFailure: action.agreeToTermsFailure,
				},
			};
		case 'UPDATE_CONFIRM_REQUEST':
			return {
				...state,
				personalDetails: {
					...state.personalDetails,
					confirmRequest: action.confirmRequest,
					confirmRequestFailure: action.confirmRequestFailure,
				},
			};
		default:
			return state;
	}
}

function weekDataForCurrentState( property: Property, startOfStay: Date, numberOfNights: number ): WeekData | null {
	const year = startOfStay.getUTCFullYear();
	const month = startOfStay.getUTCMonth() + 1;
	const date = startOfStay.getUTCDate();

	// One bag of unique weeks. Values are fully copied.
	const allWeeks = [
		...property.thisMonth,
		...property.nextMonth,
	].filter( ( week, index, self ) => {
		return self.findIndex( ( x ) => {
			return x.date === week.date && x.weekNumber === week.weekNumber && x.month === week.month && x.year === week.year;
		} ) === index;
	} ).map( ( week ) => {
		return {
			...week,
		};
	} );

	const firstWeekIndex = allWeeks.findIndex( ( week ) => {
		return date === week.date && month === week.month && year === week.year;
	} );

	const neededWeeks = allWeeks.slice( firstWeekIndex, firstWeekIndex + numberOfNights / 7 );

	if ( neededWeeks.length !== numberOfNights / 7 ) {
		return null;
	}

	return {
		allowNewReservations: property.allowNewReservations,
		weeks: neededWeeks,
	};
}
